<template>
    <kiosk-layout>
        <div>
            <page-header>Phone Numbers</page-header>

            <div class="flex flex-col gap-12 lg:flex-row">
                <div class="shrink-0 lg:w-60">
                    <kiosk-tcr-phone-numbers-sidebar v-model="fetchParams"></kiosk-tcr-phone-numbers-sidebar>
                </div>
                <div class="w-full">
                    <kiosk-ten-dlc-controls
                        v-model="fetchParams"
                        placeholder="Search phone number"
                        @apply="applyFilters"
                        @reset="resetFilters"
                    ></kiosk-ten-dlc-controls>

                    <page-loading v-if="loading && !list.length"></page-loading>

                    <empty-state
                        v-else-if="!list.length && !isEqual(defaultFilters(), fetchParams)"
                        @reset="resetFilters"
                    />

                    <empty-state v-else-if="!list.length"> No phone numbers found. </empty-state>

                    <div v-if="list.length">
                        <kiosk-tcr-phone-numbers-table>
                            <kiosk-tcr-phone-number-row
                                v-for="phoneNumber in list"
                                :key="phoneNumber.phone_number"
                                :modelValue="phoneNumber"
                            ></kiosk-tcr-phone-number-row>
                        </kiosk-tcr-phone-numbers-table>

                        <div v-if="fetchParams.cursor" class="mt-8">
                            <vx-button
                                @click="loadData()"
                                color="muted-dark"
                                size="xl"
                                class="grow-0"
                                data-test="load-more-btn"
                                :loading="loading"
                            >
                                Load More
                                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                            </vx-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton } from '@voxie/frontend-components';
import { watch } from 'vue';
import EmptyState from '~/components/general/EmptyState.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTenDlcControls from '~/components/kiosk/kiosk-ten-dlc/KioskTenDlcControls.vue';
import KioskTcrPhoneNumberRow from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrPhoneNumberRow.vue';
import KioskTcrPhoneNumbersSidebar from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrPhoneNumbersSidebar.vue';
import KioskTcrPhoneNumbersTable from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrPhoneNumbersTable.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { adminTenDlcClient } from '~/services';
import type { TcrListParams, TcrPhoneNumber } from '~/services/adminTenDlcClient.types';
import { isEqual } from '~/utils/helpers';

// Every sidebar key must be present (as undefined) so isEqual() against the
// live fetchParams keeps working after the sidebar writes undefined values.
const defaultFilters = (): TcrListParams => ({
    query: '',
    vendor: undefined,
    brand_id: undefined,
    campaign_id: undefined,
    team_id: undefined,
    sort_field: 'phone_number',
    sort_dir: 'asc',
    per_page: 25,
    cursor: null,
});

const { loading, list, fetchParams, fetchRecords } = useCursorPagination<TcrPhoneNumber, TcrListParams>();

const listCancelKey = Symbol('kiosk-tcr-phone-numbers');

const loadData = () => {
    fetchRecords(adminTenDlcClient.getTcrPhoneNumbers(fetchParams.value, { cancelKey: listCancelKey }));
};

// Deep-watching fetchParams would loop forever: fetchRecords writes the next
// cursor back into it after every response. Watch the sidebar filters by name.
watch(
    [
        () => fetchParams.value.vendor,
        () => fetchParams.value.brand_id,
        () => fetchParams.value.campaign_id,
        () => fetchParams.value.team_id,
    ],
    () => {
        fetchParams.value.cursor = null;
        loadData();
    }
);

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

resetFilters();
</script>
